import { logger } from '@/utils/logger';
import { PinState, GpsData, Project, User } from '@/types/index';
import { safeCanvasToBlob, safeCanvasToDataURL } from '@/utils/compat';

/**
 * Enhanced image processing utilities for the professional watermark system.
 *
 * Responsibilities:
 *  - Watermark theme / layout / arrow / performance configuration constants.
 *  - Image-optimization helpers (compress, resize, lazy-load).
 *  - In-memory caches plus performance/memory monitoring.
 *  - Pin-state validation and defaults used by the positioning UI.
 *
 * Performance optimizations: image compression, lazy loading, caching, and
 * periodic memory management.
 *
 * NOTE: Only `validatePinState` and `getDefaultPinState` are consumed outside
 * this module today, but every exported accessor is part of the public surface
 * and must be preserved.
 */

// ---------------------------------------------------------------------------
// Configuration constants
// ---------------------------------------------------------------------------

/** PinCam brand theme. Values are load-bearing for watermark rendering. */
const PINCAM_THEME = {
  PRIMARY: '#FFD700', // Yellow
  SECONDARY: '#000000', // Black
  BACKGROUND: 'rgba(0, 0, 0, 0.9)',
  TEXT_PRIMARY: '#FFD700',
  TEXT_SECONDARY: '#FFFFFF',
  BORDER: '#FFD700',
  SHADOW: 'rgba(0, 0, 0, 0.8)',
  OVERLAY: 'rgba(0, 0, 0, 0.7)',
} as const;

/** Enhanced arrow rendering configuration. */
const ARROW_CONFIG = {
  SIZE: 40,
  COLOR: PINCAM_THEME.PRIMARY,
  BORDER_COLOR: PINCAM_THEME.SECONDARY,
  BORDER_WIDTH: 3,
  SHADOW_COLOR: PINCAM_THEME.SHADOW,
  SHADOW_BLUR: 6,
  ARROW_LENGTH: 25,
  ARROW_WIDTH: 8,
  PREVIEW_SIZE: 30, // Smaller size for preview
  PREVIEW_BORDER_WIDTH: 2,
} as const;

/** Watermark column layout configuration. */
const WATERMARK_LAYOUT = {
  COLUMN_WIDTH_RATIO: 0.3, // 30% of total width
  PADDING: 20,
  SECTION_SPACING: 15,
  FLOOR_PLAN_SIZE_RATIO: 0.8, // 80% of column width
  FONT_FAMILY: '"Courier New", Courier, monospace',
  TITLE_FONT_SIZE: 24,
  BODY_FONT_SIZE: 16,
  SMALL_FONT_SIZE: 12,
  BORDER_RADIUS: 8,
  BORDER_WIDTH: 2,
  ARROW_PREVIEW_OPACITY: 0.9, // Ensure arrow is visible in preview
} as const;

/** Performance / optimization tuning. */
const PERFORMANCE_CONFIG = {
  MAX_IMAGE_WIDTH: 1920,
  MAX_IMAGE_HEIGHT: 1080,
  COMPRESSION_QUALITY: 0.85,
  MOBILE_COMPRESSION_QUALITY: 0.75,
  CACHE_SIZE: 10, // Number of cached images
  LAZY_LOAD_THRESHOLD: 100, // ms delay for lazy loading
  MEMORY_CLEANUP_INTERVAL: 30000, // 30 seconds
} as const;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface LayoutDimensions {
  photoWidth: number;
  watermarkColumnWidth: number;
  totalWidth: number;
  height: number;
  padding: number;
  sectionSpacing: number;
  originalWidth: number;
  originalHeight: number;
  optimized: boolean;
}

interface DeviceOptimization {
  maxWidth: number;
  maxHeight: number;
  quality: number;
  enableCompression: boolean;
}

interface ImageOptimizationOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
}

/** Shape of the non-standard `performance.memory` (Chromium only). */
interface PerformanceMemory {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
}

/**
 * Narrowing helper for the non-standard, Chromium-only `performance.memory`
 * field. Returns `null` on engines that do not expose it (Safari/Firefox).
 */
function readPerformanceMemory(): PerformanceMemory | null {
  if (!('memory' in performance)) return null;
  const memory = (performance as Performance & { memory?: PerformanceMemory }).memory;
  return memory ?? null;
}

/** Text styling options for {@link drawThemedText}. */
interface ThemedTextOptions {
  fontSize?: number;
  color?: string;
  fontFamily?: string;
  textAlign?: CanvasTextAlign;
  fontWeight?: string;
}

// ---------------------------------------------------------------------------
// Caches and monitoring state
// ---------------------------------------------------------------------------

/** Image cache (data URLs keyed by caller-defined strings) for reuse. */
const imageCache = new Map<string, string>();
/** Canvas cache for reuse; cleared alongside {@link imageCache}. */
const canvasCache = new Map<string, HTMLCanvasElement>();

// Performance monitoring
let processingStartTime = 0;

/**
 * Performance monitoring utilities.
 */
const performanceUtils = {
  startTimer: (): void => {
    processingStartTime = performance.now();
    logger.debug('Image processing started');
  },

  endTimer: (operation: string): number => {
    const duration = performance.now() - processingStartTime;
    logger.info('Image processing completed', {
      operation,
      duration: `${duration.toFixed(2)}ms`,
    });
    return duration;
  },

  checkMemoryUsage: (): void => {
    const memory = readPerformanceMemory();
    if (!memory) return;

    const usedMB = memory.usedJSHeapSize / 1024 / 1024;
    const totalMB = memory.totalJSHeapSize / 1024 / 1024;

    // Only warn if memory usage is very high (90% instead of 80%)
    if (usedMB > totalMB * 0.9) {
      logger.warn('High memory usage detected', {
        usedMB: usedMB.toFixed(1),
        totalMB: totalMB.toFixed(1),
        module: 'App',
      });
      cacheUtils.cleanupCache();
    }
  },

  optimizeForDevice: (): DeviceOptimization => {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    );
    return {
      maxWidth: isMobile ? 1280 : PERFORMANCE_CONFIG.MAX_IMAGE_WIDTH,
      maxHeight: isMobile ? 720 : PERFORMANCE_CONFIG.MAX_IMAGE_HEIGHT,
      quality: isMobile
        ? PERFORMANCE_CONFIG.MOBILE_COMPRESSION_QUALITY
        : PERFORMANCE_CONFIG.COMPRESSION_QUALITY,
      enableCompression: true,
    };
  },
};

/**
 * Cache management utilities.
 */
const cacheUtils = {
  addToCache: (key: string, data: string): void => {
    if (imageCache.size >= PERFORMANCE_CONFIG.CACHE_SIZE) {
      const firstKey = imageCache.keys().next().value;
      if (firstKey) {
        imageCache.delete(firstKey);
      }
    }
    imageCache.set(key, data);
  },

  getFromCache: (key: string): string | undefined => {
    return imageCache.get(key);
  },

  cleanupCache: (): void => {
    imageCache.clear();
    canvasCache.clear();
    logger.debug('Image cache cleared');
  },
};

/**
 * Image optimization utilities.
 */
const imageOptimization = {
  compressImage: async (
    imageData: string,
    options: ImageOptimizationOptions = {},
  ): Promise<Blob> => {
    const { maxWidth, maxHeight, quality } = {
      ...performanceUtils.optimizeForDevice(),
      ...options,
    };

    return new Promise<Blob>((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas context not available'));
        return;
      }

      const img = new Image();
      img.onload = () => {
        // Calculate optimal dimensions
        let { width, height } = img;

        if (width > maxWidth || height > maxHeight) {
          const aspectRatio = width / height;
          if (width > height) {
            width = maxWidth;
            height = width / aspectRatio;
          } else {
            height = maxHeight;
            width = height * aspectRatio;
          }
        }

        canvas.width = width;
        canvas.height = height;

        // Use high-quality rendering
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        // safeCanvasToBlob never leaves the promise pending: iOS Safari can
        // invoke toBlob's callback with null, which the old code dropped on the
        // floor and hung the caller forever.
        safeCanvasToBlob(canvas, 'image/jpeg', quality).then((blob) => {
          if (blob) resolve(blob);
          else reject(new Error('Failed to encode image'));
        });
      };
      img.onerror = () => reject(new Error('Failed to load image for compression'));

      img.src = imageData;
    });
  },

  resizeImage: async (
    imageData: string,
    targetWidth: number,
    targetHeight: number,
  ): Promise<string> => {
    return new Promise<string>((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas context not available');

      const img = new Image();
      img.onload = () => {
        canvas.width = targetWidth;
        canvas.height = targetHeight;

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

        resolve(safeCanvasToDataURL(canvas, 'image/jpeg', 0.9));
      };

      img.src = imageData;
    });
  },

  lazyLoadImage: (src: string, callback: (img: HTMLImageElement | null) => void): void => {
    const img = new Image();
    img.onload = () => callback(img);
    img.onerror = () => callback(null);

    // Add a small delay before assigning src for lazy loading.
    setTimeout(() => {
      img.src = src;
    }, PERFORMANCE_CONFIG.LAZY_LOAD_THRESHOLD);
  },
};

// ---------------------------------------------------------------------------
// Canvas drawing primitives
//
// The helpers below back the deprecated section renderers. They are retained
// (with `@deprecated` markers) because the watermark module system may reuse
// them; do not delete without confirming the module pipeline no longer needs a
// canvas-primitive fallback.
// ---------------------------------------------------------------------------

/**
 * Draw a theme-aware background for a watermark section.
 * Shadows are explicitly disabled for rendering performance.
 */
function drawSectionBackground(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  theme: typeof PINCAM_THEME = PINCAM_THEME,
): void {
  // Disable shadow for better performance.
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;

  // Draw background with theme colors.
  ctx.fillStyle = theme.BACKGROUND;
  ctx.fillRect(x, y, width, height);

  // Draw border.
  ctx.strokeStyle = theme.BORDER;
  ctx.lineWidth = WATERMARK_LAYOUT.BORDER_WIDTH;
  ctx.strokeRect(x, y, width, height);
}

/**
 * Draw text with theme styling.
 */
function drawThemedText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  options: ThemedTextOptions = {},
): void {
  const {
    fontSize = WATERMARK_LAYOUT.BODY_FONT_SIZE,
    color = PINCAM_THEME.TEXT_PRIMARY,
    fontFamily = WATERMARK_LAYOUT.FONT_FAMILY,
    textAlign = 'left',
    fontWeight = 'normal',
  } = options;

  const fontString = `${fontWeight} ${fontSize}px ${fontFamily}`;

  ctx.font = fontString;
  ctx.fillStyle = color;
  ctx.textAlign = textAlign;
  ctx.textBaseline = 'top';

  // Optimize text rendering.
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  ctx.fillText(text, x, y);
}

/**
 * Enhanced arrow drawing with better visibility.
 * @deprecated Consider using watermark modules instead. Kept for potential future use.
 */
function _drawArrow(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  direction: number,
  size: number = ARROW_CONFIG.SIZE,
  isPreview: boolean = false,
): void {
  const arrowSize = isPreview ? ARROW_CONFIG.PREVIEW_SIZE : size;
  const borderWidth = isPreview ? ARROW_CONFIG.PREVIEW_BORDER_WIDTH : ARROW_CONFIG.BORDER_WIDTH;
  const centerX = x + arrowSize / 2;
  const centerY = y + arrowSize / 2;

  ctx.save();

  // Translate and rotate to the arrow's center.
  ctx.translate(centerX, centerY);
  ctx.rotate((direction * Math.PI) / 180);

  // Arrow geometry.
  ctx.beginPath();
  ctx.moveTo(-ARROW_CONFIG.ARROW_LENGTH / 2, 0);
  ctx.lineTo(ARROW_CONFIG.ARROW_LENGTH / 2, 0);
  ctx.lineTo(ARROW_CONFIG.ARROW_LENGTH / 2 - ARROW_CONFIG.ARROW_WIDTH, -ARROW_CONFIG.ARROW_WIDTH);
  ctx.lineTo(ARROW_CONFIG.ARROW_LENGTH / 2 - ARROW_CONFIG.ARROW_WIDTH, ARROW_CONFIG.ARROW_WIDTH);
  ctx.closePath();

  // Fill arrow with high-contrast colors.
  if (isPreview) {
    // For preview: bright colors for better visibility.
    ctx.fillStyle = '#FFD700'; // Bright gold
    ctx.strokeStyle = '#000000'; // Black border
    ctx.lineWidth = borderWidth * 1.5; // Thicker border for preview
  } else {
    // For final output: theme colors.
    ctx.fillStyle = ARROW_CONFIG.COLOR;
    ctx.strokeStyle = ARROW_CONFIG.BORDER_COLOR;
    ctx.lineWidth = borderWidth;
  }

  // Draw with shadow for better visibility.
  ctx.shadowColor = ARROW_CONFIG.SHADOW_COLOR;
  ctx.shadowBlur = isPreview ? ARROW_CONFIG.SHADOW_BLUR * 2 : ARROW_CONFIG.SHADOW_BLUR;
  ctx.shadowOffsetX = 2;
  ctx.shadowOffsetY = 2;

  ctx.fill();
  ctx.stroke();

  // Add a white highlight for better contrast.
  ctx.shadowColor = 'transparent';
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
  ctx.strokeStyle = '#FFFFFF';
  ctx.lineWidth = 1;
  ctx.stroke();

  ctx.restore();
}

/**
 * Enhanced project info section drawing.
 * @deprecated Consider using watermark modules instead. Kept for potential future use.
 */
function _drawProjectInfoSection(
  ctx: CanvasRenderingContext2D,
  layout: LayoutDimensions,
  projectData: Project | null,
  userData: User | { username: string },
  startY: number,
): number {
  const sectionHeight = 120;

  drawSectionBackground(
    ctx,
    layout.photoWidth + layout.padding,
    startY,
    layout.watermarkColumnWidth - layout.padding * 2,
    sectionHeight,
  );

  const textX = layout.photoWidth + layout.padding * 2;
  let textY = startY + layout.padding;

  // Project name.
  drawThemedText(ctx, 'PROJECT:', textX, textY, {
    fontSize: WATERMARK_LAYOUT.SMALL_FONT_SIZE,
    color: PINCAM_THEME.TEXT_PRIMARY,
    fontWeight: 'bold',
  });
  textY += 20;

  drawThemedText(ctx, projectData?.name || 'Unknown Project', textX, textY, {
    fontSize: WATERMARK_LAYOUT.BODY_FONT_SIZE,
    color: PINCAM_THEME.TEXT_SECONDARY,
  });
  textY += 30;

  // Photographer.
  drawThemedText(ctx, 'PHOTOGRAPHER:', textX, textY, {
    fontSize: WATERMARK_LAYOUT.SMALL_FONT_SIZE,
    color: PINCAM_THEME.TEXT_PRIMARY,
    fontWeight: 'bold',
  });
  textY += 20;

  drawThemedText(ctx, userData?.username || 'Unknown', textX, textY, {
    fontSize: WATERMARK_LAYOUT.BODY_FONT_SIZE,
    color: PINCAM_THEME.TEXT_SECONDARY,
  });

  return sectionHeight + layout.sectionSpacing;
}

/**
 * Enhanced GPS section drawing.
 * @deprecated Consider using watermark modules instead. Kept for potential future use.
 */
function _drawGPSSection(
  ctx: CanvasRenderingContext2D,
  layout: LayoutDimensions,
  gpsData: GpsData | null,
  startY: number,
): number {
  const sectionHeight = 80;

  drawSectionBackground(
    ctx,
    layout.photoWidth + layout.padding,
    startY,
    layout.watermarkColumnWidth - layout.padding * 2,
    sectionHeight,
  );

  const textX = layout.photoWidth + layout.padding * 2;
  let textY = startY + layout.padding;

  // GPS coordinates.
  drawThemedText(ctx, 'GPS COORDINATES:', textX, textY, {
    fontSize: WATERMARK_LAYOUT.SMALL_FONT_SIZE,
    color: PINCAM_THEME.TEXT_PRIMARY,
    fontWeight: 'bold',
  });
  textY += 20;

  const gpsText =
    gpsData?.lat && gpsData?.lng
      ? `${gpsData.lat.toFixed(6)}, ${gpsData.lng.toFixed(6)}`
      : 'N/A';

  drawThemedText(ctx, gpsText, textX, textY, {
    fontSize: WATERMARK_LAYOUT.SMALL_FONT_SIZE,
    color: PINCAM_THEME.TEXT_SECONDARY,
    fontFamily: 'monospace',
  });

  return sectionHeight + layout.sectionSpacing;
}

/**
 * Enhanced text wrapping.
 * @deprecated Consider using watermark modules instead. Kept for potential future use.
 */
function _wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  fontSize: number,
): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let currentLine = words[0] || '';

  ctx.font = `${fontSize}px ${WATERMARK_LAYOUT.FONT_FAMILY}`;

  for (let i = 1; i < words.length; i++) {
    const word = words[i];
    const width = ctx.measureText(currentLine + ' ' + word).width;

    if (width < maxWidth) {
      currentLine += ' ' + word;
    } else {
      lines.push(currentLine);
      currentLine = word;
    }
  }

  lines.push(currentLine);
  return lines;
}

// `void`-referencing the deprecated helpers keeps them compiled and type-checked
// under `noUnusedLocals` without altering runtime behavior. Remove this line if
// the helpers above are deleted.
void _drawArrow;
void _drawProjectInfoSection;
void _drawGPSSection;
void _wrapText;

// ---------------------------------------------------------------------------
// Public API: pin state
// ---------------------------------------------------------------------------

/**
 * Validate a pin state.
 *
 * Defensively rejects `null`/missing positions and out-of-range (0-100)
 * coordinates. The `null` coordinate checks guard against legacy/persisted data
 * even though the {@link PinState} type marks `pos.x`/`pos.y` as `number`.
 */
export const validatePinState = (pinState: PinState | null): boolean => {
  if (!pinState) return false;
  if (!pinState.pos) return false;
  const { x, y } = pinState.pos;
  // Guard legacy data that may have persisted null coordinates.
  if (x === null || y === null) return false;
  if (x < 0 || x > 100) return false;
  if (y < 0 || y > 100) return false;
  return true;
};

/**
 * Get the default pin state, centered on a 24x24 grid.
 */
export const getDefaultPinState = (): PinState => ({
  pos: { x: 50, y: 50 },
  direction: 0,
  gridPos: {
    cellX: 12, // Center of 24x24 grid
    cellY: 12,
    offsetX: 0.5,
    offsetY: 0.5,
  },
});

// ---------------------------------------------------------------------------
// Public API: configuration / utility accessors
// ---------------------------------------------------------------------------

/** Export theme configuration for external use. */
export const getWatermarkTheme = (): typeof PINCAM_THEME => PINCAM_THEME;

/** Export layout configuration for external use. */
export const getWatermarkLayout = (): typeof WATERMARK_LAYOUT => WATERMARK_LAYOUT;

/** Export performance utilities for external use. */
export const getPerformanceUtils = (): typeof performanceUtils => performanceUtils;

/** Export cache utilities for external use. */
export const getCacheUtils = (): typeof cacheUtils => cacheUtils;

/** Export image optimization utilities for external use. */
export const getImageOptimization = (): typeof imageOptimization => imageOptimization;

/**
 * Cleanup function for memory management. Clears caches and re-checks memory.
 */
export const cleanupImageResources = (): void => {
  cacheUtils.cleanupCache();
  performanceUtils.checkMemoryUsage();
  logger.info('Image resources cleaned up');
};

// Set up periodic memory cleanup (every 30 seconds).
setInterval(() => {
  performanceUtils.checkMemoryUsage();
}, PERFORMANCE_CONFIG.MEMORY_CLEANUP_INTERVAL);
